/**
 * Download de arquivos hospedados no CDN (Spaces).
 *
 * Um `<a href="https://...spaces.../doc.pdf" download>` NÃO baixa: o atributo `download`
 * é ignorado quando o arquivo está em outra origem, então o link vira uma navegação
 * comum. Com `target="_blank"` e disparado fora de um gesto do usuário (ex.: callback do
 * Pusher quando o PDF fica pronto), o popup blocker cancela a abertura — e o usuário não
 * vê nada acontecer.
 *
 * Buscar o arquivo e salvá-lo a partir de um `blob:` URL resolve os dois problemas: a URL
 * passa a ser da mesma origem (o `download` volta a valer, com nome de arquivo) e não há
 * aba nova para o popup blocker barrar.
 */

/** Baixa o arquivo de fato (blob). Retorna `false` se o fetch não foi possível (CORS/rede). */
export async function downloadFileFromUrl(url: string, fileName?: string): Promise<boolean> {
    try {
        const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
        if (!response.ok) return false;

        const blob = await response.blob();
        const objectURL = URL.createObjectURL(blob);

        clickAnchor(objectURL, { download: fileName ?? fileNameFromUrl(url) });

        // Revoga depois: revogar imediatamente cancelaria o download em andamento.
        setTimeout(() => URL.revokeObjectURL(objectURL), 60_000);

        return true;
    } catch {
        return false;
    }
}

/**
 * Fallback: abre o arquivo em nova aba. Só funciona dentro de um gesto do usuário
 * (clique) — fora dele o popup blocker barra.
 */
export function openFileInNewTab(url: string): void {
    clickAnchor(url, { newTab: true });
}

/** Nome do arquivo a partir do último segmento da URL (sem query string). */
export function fileNameFromUrl(url: string): string {
    const path = url.split('?')[0];
    const name = path.substring(path.lastIndexOf('/') + 1);
    return name || 'download.pdf';
}

/**
 * Transforma o título do documento em nome de arquivo (`Relatório 01/2026` →
 * `Relatório 01-2026.pdf`). Sem título, cai no nome vindo da URL — que é a key do
 * Spaces (`<documentId>.pdf`), pouco amigável, mas nunca vazio.
 */
export function pdfFileNameFromTitle(title: string | undefined, url: string): string {
    const clean = (title ?? '')
        // Caracteres proibidos em nome de arquivo no Windows/macOS/Linux.
        .replace(/[/\\?%*:|"<>]/g, '-')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 120);

    return clean ? `${clean}.pdf` : fileNameFromUrl(url);
}

function clickAnchor(href: string, options: { download?: string; newTab?: boolean }): void {
    const anchor = document.createElement('a');
    anchor.href = href;

    if (options.download !== undefined) anchor.download = options.download;
    if (options.newTab) {
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
    }

    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
}
